import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, Divider, useMediaQuery, useTheme, IconButton, Tooltip } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { ENDPOINTS } from "./api/endpoints";
import { useParams } from "react-router-dom";
import Navbar from "./navigation";
import TalentSidebar from "./talent/components/TalentSidebar";
import MediaGallery from "./talent/components/MediaGallery";
import VideoReels from "./talent/components/VideoReels";
import AudioReels from "./talent/components/AudioReels";
import ResumeProjectsSection from "./talent/components/ResumeProjectsSection";
import ResumeTextSections from "./talent/components/ResumeTextSections";
import RightNav from "./talent/components/RightNav";
import MessagePrompt from "./components/MessagePrompt";
import ContactDialog from "./talent/components/ContactDialog";
import api from "./api/api";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import PlaceIcon from "@mui/icons-material/Place";
import GlobalNotification from "./components/GlobalNotification";
import AdminNav from "./admin/AdminNav";

const TalentView = () => {
  const { talentId } = useParams();
  const userId = talentId;

  const [talentData, setTalentData] = useState({});
  const [affiliations, setAffiliations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vocalTypes, setVocalTypes] = useState([]);
  const [vocalRange, setVocalRange] = useState([]);
  const [media, setMedia] = useState([]);
  const [groupedProjects, setGroupedProjects] = useState([]);
  const [resumeSections, setResumeSections] = useState([]);
  const theme = useTheme();
  const [headings, setHeadings] = useState([]);
  const [representationInfoList, setRepresentationInfoList] = useState({});

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [contactOpen, setContactOpen] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);
  const [favouriteLoading, setFavouriteLoading] = useState(false);

  // The logged-in director/client ID is used by the existing Search favourite API.
  const clientId = localStorage.getItem("userId");
  const [msgOpen, setMsgOpen] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const userRole = localStorage.getItem("userRole");

  // Load this talent's current favourite status using the same endpoint as Search.

  useEffect(() => {
    setIsFavourite(false);

    if (!clientId || !userId || !["COMPANY", "ADMIN"].includes(userRole)) {
      return;
    }

    let cancelled = false;

    const loadFavouriteStatus = async () => {
      try {
        const response = await api.get(
          ENDPOINTS.isClientFavoriteTalent({
            clientId,
            talentId: userId,
          })
        );

        const isFavorite = response?.data?.data?.isFavorite;

        if (!cancelled && typeof isFavorite === "boolean") {
          setIsFavourite(isFavorite);
        }
      } catch (error) {
        console.error("Error loading talent favourite status:", error);
      }
    };

    loadFavouriteStatus();

    return () => {
      cancelled = true;
    };
  }, [clientId, userId, userRole]);


  const handleFavouriteClick = async () => {
    if (!clientId || !userId || favouriteLoading) return;

    const previousValue = isFavourite;
    const nextValue = !previousValue;

    // Update the UI immediately, before waiting for the API.
    setIsFavourite(nextValue);
    setFavouriteLoading(true);

    const formData = new FormData();
    formData.append("clientId", clientId);
    formData.append("talentId", userId);
    formData.append("Fave", String(nextValue));

    try {
      await api.post(ENDPOINTS.addOrUpdateFave(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      // Roll back only if the request fails.
      setIsFavourite(previousValue);
      console.error("Error updating talent favourite:", error);
    } finally {
      setFavouriteLoading(false);
    }
  };





  useEffect(() => {
    if (!userId) return;

    const updateResumeViews = async () => {
      try {
        await api.post("/api/SearchNotice/add-or-updateResumeViews", {
          messageId: 0,
          fromEmail: "noreply@directsubmit.com",
          toEmail: "",
          subject: "A Casting Director has Reviewed your Talent Resume!",
          message: "Dear Talent,<br><br>This is a quick notification that a Casting Director or Client of Directsubmit.com has reviewed Your Talent Resume.<br><br>Please login to update any Pictures, Resume Credits, Video or Audio if you think its needed. We want you to get those Auditions! <br><br>Thanks,<br>Directubmit.com<br>www.nycastings.com<br><br>See whats casting now <a href=https://www.nycastings.com/auditions-casting-calls/>Click Here</a><br><br>Lost your password? <a href=https://www.nycastings.com/user-login/user-login-forgot/>Get your password here</a>.<br><br><font size=1><strong>WHY AM I RECEIVING THESE EMAILS?</strong> NYCastings.com recently implemented a NO-COST talent-demanded feature to notify them if <strong>any person</strong> views their profile resume. <br>If a casting director wishes to pursue you further, they will contact you direct through your resume. There is no need to call us about this notification. This is a FREE service!</font><br><br><font size=1>CD Visitor Code:166018950TR01120611912292024 1:49:57 PM (ST)</font> <br><font size=1>All information provided is protected by Our Site <a href=https://www.nycastings.com/terms-of-service/>Terms of Service</a> and <a href=https://www.nycastings.com/privacy-policy/>Privacy Policy</a></font>",
          date: new Date().toISOString(),
          status: "Viewed",
          noticeId: 0,
          userId: Number(userId),
          activeFlag: true,
          phoneNumber: ""
        });
      } catch (error) {
        console.error("Error updating resume views:", error);
      }
    };

    updateResumeViews();
  }, [userId]);
  useEffect(() => {
    if (!userId) return;
    const loadAll = async () => {
      try {
        const [
          profileRes,
          affRes,
          locRes,
          vocalRes,
          mediaRes,
          projRes,
          textRes,
          headingsRes,
          vocalRangeRes
        ] = await Promise.all([
          api.get(`api/TalentProfile/get-talent-profile/${userId}`),
          api.get(`api/TalentProfile/GetUserAffiliations/${userId}`),
          api.get(`api/User/GetLocations`),
          api.get(`api/TalentProfile/GetUserVocalTypes/${userId}`),
          api.get(`api/TalentProfile/GetUserMedia/${userId}`),
          api.get(`api/TalentProfile/GetResumeSectionsWithDetails/${userId}`),
          api.get(`api/TalentProfile/GetUserResumeTextData/${userId}`),
          api.get(`api/User/get-all-representation-headings`),
          api.get(`api/User/get-vocal-ranges`)
        ]);

        const vocalRangeList = Array.isArray(vocalRangeRes?.data?.data)
          ? vocalRangeRes.data.data
          : Array.isArray(vocalRangeRes?.data)
            ? vocalRangeRes.data
            : [];

        const vocalTypeList = Array.isArray(vocalRes?.data)
          ? vocalRes.data.filter(v => v.activeFlag).map(v => v.vocalTypeName)
          : [];

        setVocalRange(vocalRangeList);
        setVocalTypes(vocalTypeList);
        setTalentData(profileRes?.data?.data || {});
        setAffiliations(affRes?.data || []);
        setLocations(locRes?.data?.data || []);

        const normalizedMedia = (mediaRes?.data || [])
          .filter(m => m.activeFlag === 1)
          .map(m => ({
            ...m,
            mediaName: m.mediaName || m.mediaLink
          }));

        setMedia(normalizedMedia);
        setGroupedProjects(projRes?.data || []);
        setResumeSections(textRes?.data || []);

        const validHeadings = headingsRes?.data?.data || headingsRes?.data || [];
        const headingNames = validHeadings.map(h => h.headingName);
        setHeadings(headingNames);

      } catch (err) {
        console.error("Error loading talent view data", err);
      }
    };

    loadAll();
  }, [userId]);

  useEffect(() => {
    if (!userId || headings.length === 0) return;


    const loadRepresentation = async () => {
      const reps = {};

      await Promise.all(
        headings.map(async (headingName) => {
          try {
            const res = await api.get(
              `api/TalentProfile/GetRepresentationInformation/${userId}/${headingName}`
            );

            const raw = res.data?.data ?? res.data;

            reps[headingName] = Array.isArray(raw)
              ? raw.filter(r => r && Object.keys(r).length > 0)
              : raw && Object.keys(raw).length > 0
                ? [raw]
                : [];
          } catch {
            reps[headingName] = [];
          }
        })
      );

      setRepresentationInfoList(reps);
    };

    loadRepresentation();
  }, [userId, headings]);


  // -------- Media grouping --------
  const pictureItems = useMemo(
    () => media.filter(m => m.mediaType?.toLowerCase() === "picture"),
    [media]
  );

  const videoItems = useMemo(
    () =>
      media.filter(m =>
        ["ytvideo", "vimvideo", "video"].includes(m.mediaType?.toLowerCase())
      ),
    [media]
  );

  const audioItems = useMemo(
    () => media.filter(m => m.mediaType?.toLowerCase() === "audio"),
    [media]
  );

  const profilePic = media.find(
    m => m.mediaType?.toLowerCase() === "profile"
  )?.mediaName;

  const hasAnyMedia =
    pictureItems.length > 0 ||
    videoItems.length > 0 ||
    audioItems.length > 0;

  const hasCredits = groupedProjects.length > 0;
  const hasTextSections = resumeSections.length > 0;

  const showRightNav = hasAnyMedia || hasCredits || hasTextSections;

  const locationLabel =
    [talentData?.city, talentData?.location].filter(Boolean).join(", ") ||
    "Location";

  const contact = {
    phone:
      talentData.primaryContactNumber,

    secondary:
      talentData.secondaryContactNumber,

    email:
      talentData.email,

    name:
      `${talentData.firstName || ""} ${talentData.lastName || ""}`.trim(),

    personalWebSite:
      talentData.personalWebSite,

    instagram:
      talentData.instagram,

    twitter:
      talentData.twitter,

    faceBook:
      talentData.faceBook,

    tikTok:
      talentData.tikTok,

    youTube:
      talentData.youTube,

    imdb:
      talentData.imdb,

    snapChat:
      talentData.snapChat,

    paymentLink1:
      talentData.paymentLink1,

    paymentLink2:
      talentData.paymentLink2,

    paymentLink3:
      talentData.paymentLink3,
  };

  return (
    <>
      {userRole === "ADMIN" ? <AdminNav /> : <Navbar />}
      <Box sx={{ maxWidth: 1200, mx: "auto", py: 2 }}>
        {/*          <GlobalNotification userId={userId} userRole={userRole} /> */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1
          }}
        >
          {isMobile &&
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PlaceIcon fontSize="medium" sx={{ mr: 1, color: "#f00" }} />
                <Typography variant="h7" sx={{ fontWeight: 700 }}>
                  {locationLabel}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {["COMPANY", "ADMIN"].includes(userRole) && (
                  <Tooltip title={isFavourite ? "Remove from favourites" : "Add to favourites"}>
                    <span>

                      <IconButton
                        onClick={handleFavouriteClick}
                        disabled={favouriteLoading}
                        aria-label={
                          isFavourite ? "Remove from favourites" : "Add to favourites"
                        }
                        sx={{
                          color: isFavourite ? "red" : "text.secondary",
                          transition: "none",
                        }}
                      >
                        {isFavourite ? (
                          <FavoriteIcon sx={{ color: "red" }} />
                        ) : (
                          <FavoriteBorderIcon sx={{ color: "inherit" }} />
                        )}
                      </IconButton>

                    </span>
                  </Tooltip>
                )}
                <Button
                  variant="outlined"
                  onClick={() => setMsgOpen(true)}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  CONTACT
                </Button>
              </Box>
            </Box>

          }
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: "100%", md: "250px" },
              minWidth: { xs: "100%", md: "250px" },
              flexShrink: 0
            }}
          >
            <TalentSidebar
              profilePic={profilePic}
              talentData={talentData}
              affiliations={affiliations}
              locations={locations}
              vocalTypes={vocalTypes}
              vocalRange={vocalRange}
              sidebarMediaEditMode={false}
              setEditPromptOpen={() => { }}
            />
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, position: "relative" }}>
            <Box
              sx={{
                flexGrow: 1,
                marginTop: !isMobile && hasAnyMedia ? "0" : 0,
                width: "100%",
                position: "relative"
              }}
            ></Box>
            {/* Header */}

            {!isMobile &&
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PlaceIcon fontSize="medium" sx={{ mr: 1, color: "#f00" }} />
                  <Typography variant="h7" sx={{ fontWeight: 700 }}>
                    {locationLabel}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {["COMPANY", "ADMIN"].includes(userRole) && (
                    <Tooltip title={isFavourite ? "Remove from favourites" : "Add to favourites"}>
                      <span>

                        <IconButton
                          onClick={handleFavouriteClick}
                          disabled={favouriteLoading}
                          aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                          disableRipple
                          sx={{
                            "&:hover": { backgroundColor: "transparent" },
                            "&:active": { backgroundColor: "transparent" },
                            "&.Mui-focusVisible": { backgroundColor: "transparent" },
                          }}
                        >
                          {isFavourite ? (
                            <FavoriteIcon sx={{ color: isFavourite ? "error.main" : "text.secondary" }} />
                          ) : (
                            <FavoriteBorderIcon sx={{ color: "text.secondary" }} />
                          )}
                        </IconButton>

                      </span>
                    </Tooltip>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => setMsgOpen(true)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    CONTACT
                  </Button>
                </Box>
              </Box>

            }


            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 1
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Media */}
                <Box sx={{ backgroundColor: "#fff", borderRadius: "5px" }}>
                  <Box id="pictures" sx={{ scrollMarginTop: "80px" }}>
                    {pictureItems.length > 0 && (
                      <MediaGallery
                        pictures={pictureItems}
                        onImageClick={idx => {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }}
                      />
                    )}
                  </Box>

                  {videoItems.length > 0 && (
                    <>
                      {pictureItems.length > 0 && <Divider sx={{ my: 1 }} />}
                      <Box id="video" sx={{ scrollMarginTop: "80px" }}>
                        <VideoReels videoItems={videoItems} />
                      </Box>
                    </>
                  )}

                  {audioItems.length > 0 && (
                    <>
                      {(pictureItems.length > 0 || videoItems.length > 0) && (
                        <Divider sx={{ my: 1 }} />
                      )}
                      <Box id="audio" sx={{ scrollMarginTop: "80px" }}>
                        <AudioReels audioItems={audioItems} />
                      </Box>
                    </>
                  )}
                </Box>

                {/* Credits */}
                {hasCredits && (
                  <Box
                    id="credits"
                    sx={{
                      mt: 0.5,
                      backgroundColor: "#fff",
                      borderRadius: "5px",
                      scrollMarginTop: "80px"
                    }}
                  >
                    <ResumeProjectsSection
                      groupedResumeProjects={groupedProjects}
                      editMode={false}
                    />
                  </Box>
                )}

                {/* Text Sections */}
                {hasTextSections && (
                  <Box id="textSections" sx={{ scrollMarginTop: "80px" }}>
                    <ResumeTextSections
                      resumeSections={resumeSections}
                      resumeEditMode={false}
                      showEmptySections={false}
                    />
                  </Box>
                )}
              </Box>

              {showRightNav && !isMobile && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 50,
                    alignSelf: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  <RightNav />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Lightbox */}
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={pictureItems.map(item => ({ src: item.mediaName }))}
        />


        {/*       <ContactDialog
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          contact={contact}
          headings={headings}
          representationInfoList={representationInfoList}
          editMode={false}
        />  */}
        <MessagePrompt
          open={msgOpen}
          onClose={() => setMsgOpen(false)}
          onSent={() => setMsgOpen(false)}
          contact={contact}
          headings={headings}
          representationInfoList={representationInfoList}
          talentId={Number(talentId)}
          defaultSubject="Direct message"
          editorHeight={150}
        />
      </Box>
    </>
  );
};

export default TalentView;